import { PaymentType } from '@allesauto/graphql';
import { consts, InitialPriceFilterState } from '@allesauto/utils/consts';
import { isEmptyObject, toUrlJSON } from '@allesauto/utils/funcs';
import isArray from 'lodash/isArray';
import isNil from 'lodash/isNil';

import { getDefaultPriceRange } from '.';
import { IFacet, PCPReduxState, RouteFilter } from '../types';

export const getQueryParams = (
  state: PCPReduxState,
  facets: IFacet,
  routeFilter?: RouteFilter
) => {
  const [ROUTE_FILTER_KEY, ROUTE_FILTER_VALUE] =
    (routeFilter && Object.entries(routeFilter)[0]) || [];
  const defaultPriceRange = getDefaultPriceRange(facets.price);
  const queryParams: any = {};

  if (!isEmptyObject(state.filterAccordionOpenStates)) {
    queryParams.filterAccordionStates = toUrlJSON(
      state.filterAccordionOpenStates
    );
  }

  if (!isEmptyObject(state.filterBodyColors)) {
    queryParams.bodyColors = toUrlJSON(state.filterBodyColors, false, [
      'filterCount',
    ]);
  }

  if (
    !isEmptyObject(state.filterInterior.interiorType) ||
    !isEmptyObject(state.filterInterior.interiorColor)
  ) {
    queryParams.interior = toUrlJSON(state.filterInterior, false, [
      'filterCount',
    ]);
  }

  if (
    !isEmptyObject(state.filterVehicleType.vehicleType) ||
    !isEmptyObject(state.filterVehicleType.doors) ||
    state.filterVehicleType.maxSeats ||
    state.filterVehicleType.minSeats ||
    ROUTE_FILTER_KEY === 'vehicleType'
  ) {
    queryParams.vehicleType = toUrlJSON(state.filterVehicleType, false, [
      'filterCount',
    ]);
  }

  if (
    !isEmptyObject(state.filterEquipment.comfort) ||
    !isEmptyObject(state.filterEquipment.entertainment) ||
    !isEmptyObject(state.filterEquipment.security) ||
    !isEmptyObject(state.filterEquipment.aircon)
  ) {
    queryParams.equipment = toUrlJSON(state.filterEquipment, false, [
      'filterCount',
    ]);
  }

  if (
    state.filterPriceRange.key !== PaymentType.CashRange ||
    (state.filterPriceRange.max !== InitialPriceFilterState.max &&
      state.filterPriceRange.max !== defaultPriceRange?.max) ||
    (state.filterPriceRange.min !== InitialPriceFilterState.min &&
      state.filterPriceRange.min !== defaultPriceRange?.min)
  ) {
    queryParams.priceRange = toUrlJSON(state.filterPriceRange, false);
  }

  if (
    !isEmptyObject(state.filterDrive) &&
    (state.filterDrive.gearBox.length > 0 ||
      // state.filterDrive.power.key !== drive.power[0].key ||
      state.filterDrive.power.min ||
      state.filterDrive.power.max ||
      state.filterDrive.maxCubicCapacity)
  ) {
    queryParams.drive = toUrlJSON(
      {
        ...(state.filterDrive.gearBox.length && {
          gearBox: state.filterDrive.gearBox,
        }),
        ...((state.filterDrive.power.min || state.filterDrive.power.max) && {
          power: {
            key: state.filterDrive.power.key,
            min: state.filterDrive.power.min,
            max: state.filterDrive.power.max,
          },
        }),
        ...(state.filterDrive.maxCubicCapacity && {
          maxCubicCapacity: state.filterDrive.maxCubicCapacity,
        }),
      },
      false
    );
  }

  if (
    !isEmptyObject(state.filterLocation) &&
    !isEmptyObject(state.filterLocation.location) &&
    !state.filterLocation.latitude !== undefined &&
    !state.filterLocation.longitude !== undefined
  ) {
    queryParams.location = toUrlJSON(
      {
        location: state.filterLocation.location,
        radius: state.filterLocation.radius,
        latitude: state.filterLocation.latitude,
        longitude: state.filterLocation.longitude,
      },
      false,
      ['filterCount']
    );
  }

  if (!isEmptyObject(state.filterMerchantLocation.merchantLocation)) {
    queryParams.merchantLocation = toUrlJSON(
      state.filterMerchantLocation,
      false,
      ['filterCount']
    );
  }

  if (state.filterMakeModel.length) {
    const tmp: any = [];

    state.filterMakeModel.forEach((item: any) => {
      const itemToSave: any = {};

      if (item.make) itemToSave.makes = item.make;
      if (item.model) itemToSave.models = item.model;

      if (!isEmptyObject(itemToSave)) tmp.push(itemToSave);
    });

    if (!isEmptyObject(tmp)) {
      queryParams.makeModel = toUrlJSON(tmp, false, ['filterCount']);
    }
  } else if (ROUTE_FILTER_KEY === 'brands') {
    const item: any = {
      makes: routeFilter?.brands?.makeName,
    };
    if (routeFilter?.brands?.modelName) {
      item.models = routeFilter?.brands?.modelName;
    }
    queryParams.makeModel = toUrlJSON([item], false, ['filterCount']);
  }

  if (ROUTE_FILTER_KEY === 'fuelType' || !isEmptyObject(state.filterFuel)) {
    const objToSave: any = {};

    if (
      (isArray(state.filterFuel.fuelType) &&
        state.filterFuel.fuelType.length) ||
      (ROUTE_FILTER_VALUE &&
        'fuelType' in ROUTE_FILTER_VALUE &&
        ROUTE_FILTER_VALUE.fuelType)
    ) {
      objToSave.fuelType = state.filterFuel.fuelType;
    }

    if (!isNil(state.filterFuel.maxFuelConsumption)) {
      objToSave.maxFuelConsumption = state.filterFuel.maxFuelConsumption;
    }

    if (!isNil(state.filterFuel.maxPowerConsumption)) {
      objToSave.maxPowerConsumption = state.filterFuel.maxPowerConsumption;
    }

    if (!isEmptyObject(objToSave)) {
      queryParams.fuelType = toUrlJSON(objToSave, false);
    }
  }

  if (!isEmptyObject(state.filterEmission)) {
    const savingObj: any = {};

    if (!isNil(state.filterEmission.emissionClass)) {
      savingObj.emissionClass = state.filterEmission.emissionClass;
    }

    if (!isNil(state.filterEmission.co2Efficiency)) {
      savingObj.co2Efficiency = state.filterEmission.co2Efficiency;
    }

    if (
      isArray(state.filterEmission.emissionSticker) &&
      state.filterEmission.emissionSticker.length
    ) {
      savingObj.emissionSticker = state.filterEmission.emissionSticker;
    }

    if (!isNil(state.filterEmission.minCo2Emission)) {
      savingObj.minCo2Emission = state.filterEmission.minCo2Emission;
    }

    if (!isNil(state.filterEmission.maxCo2Emission)) {
      savingObj.maxCo2Emission = state.filterEmission.maxCo2Emission;
    }

    if (state.filterEmission.particleFilter) {
      savingObj.particleFilter = state.filterEmission.particleFilter;
    }

    if (!isEmptyObject(savingObj)) {
      queryParams.emission = toUrlJSON(savingObj, false);
    }
  }

  if (
    ROUTE_FILTER_KEY === 'vehicleCondition' ||
    !isEmptyObject(state.filterVehicleCondition)
  ) {
    const objToSave: any = {};

    if (
      (isArray(state.filterVehicleCondition.condition) &&
        state.filterVehicleCondition.condition.length) ||
      (ROUTE_FILTER_VALUE &&
        'condition' in ROUTE_FILTER_VALUE &&
        ROUTE_FILTER_VALUE.condition)
    ) {
      objToSave.condition = state.filterVehicleCondition.condition;
    }

    if (!isNil(state.filterVehicleCondition.mileage)) {
      objToSave.mileage = state.filterVehicleCondition.mileage;
    }

    if (!isNil(state.filterVehicleCondition.minYear)) {
      objToSave.minYear = state.filterVehicleCondition.minYear;
    }
    if (!isNil(state.filterVehicleCondition.maxYear)) {
      objToSave.maxYear = state.filterVehicleCondition.maxYear;
    }

    if (!isNil(state.filterVehicleCondition.maxOwners)) {
      objToSave.maxOwners = state.filterVehicleCondition.maxOwners;
    }

    if (!isEmptyObject(objToSave)) {
      queryParams.vehicleCondition = toUrlJSON(objToSave, false, [
        'filterCount',
      ]);
    }
  }

  if (
    !isEmptyObject(state.sorting) &&
    !(
      state.sorting.subject ===
        consts.PCP.HORIZONTAL_TOOLBAR.SORTING_DEFAULT.SUBJECT &&
      state.sorting.direction ===
        consts.PCP.HORIZONTAL_TOOLBAR.SORTING_DEFAULT.DIRECTION
    )
  ) {
    queryParams.sorting = toUrlJSON(state.sorting, false);
  }

  if (
    !isEmptyObject(state.itemsPerPage) &&
    state.itemsPerPage !==
      consts.PCP.HORIZONTAL_TOOLBAR.RESULTS_PER_PAGE_DEFAULT
  ) {
    queryParams.itemsPerPage = toUrlJSON(state.itemsPerPage, false);
  }

  return queryParams;
};
